import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    UserPlus, Sparkles, XCircle, CheckCircle2, MessageSquare, Clock,
    Rocket, IndianRupee, Globe, ShieldAlert, LifeBuoy, ArrowRight, ListTodo,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const CARDS = [
    { key: "new_doctors", label: "New doctors (7d)", icon: UserPlus, path: "/admin/doctors?days=7", tint: "green",
      valueOf: (s) => s.registrations.last_7d },
    { key: "ai_queued", label: "AI queued", icon: Sparkles, path: "/admin/ai-jobs?status=queued", tint: "blue",
      valueOf: (s) => s.ai_jobs.queued },
    { key: "ai_failed", label: "AI failed", icon: XCircle, path: "/admin/ai-jobs?status=failed", tint: "red",
      valueOf: (s) => s.ai_jobs.failed },
    { key: "pending_admin", label: "Pending admin approval", icon: ListTodo, path: "/admin/queue", tint: "amber",
      valueOf: (s) => s.profile_statuses.admin_review + s.profile_statuses.changes_requested },
    { key: "doctor_review", label: "Pending doctor approval", icon: MessageSquare, path: "/admin/doctors?status=doctor_review", tint: "amber",
      valueOf: (s) => s.profile_statuses.doctor_review },
    { key: "balance_pending", label: "Pending balance payment", icon: IndianRupee, path: "/admin/doctors?status=payment_pending", tint: "amber",
      valueOf: (s) => s.profile_statuses.payment_pending },
    { key: "ready_publish", label: "Ready to publish", icon: Rocket, path: "/admin/publishing", tint: "blue",
      valueOf: (s) => s.profile_statuses.ready_to_publish },
    { key: "published", label: "Published websites", icon: CheckCircle2, path: "/admin/doctors?status=published", tint: "green",
      valueOf: (s) => s.profile_statuses.published },
    { key: "domains", label: "Domain requests", icon: Globe, path: "/admin/domains", tint: "blue",
      valueOf: (s) => s.totals.open_domain_requests },
    { key: "amc_due", label: "AMC renewals due (30d)", icon: Clock, path: "/admin/doctors?filter=amc_due", tint: "amber",
      valueOf: (s) => s.totals.amc_due },
    { key: "tickets", label: "Open tickets", icon: LifeBuoy, path: "/admin/support", tint: "amber",
      valueOf: (s) => s.totals.open_tickets },
    { key: "attention", label: "Profiles needing attention", icon: ShieldAlert, path: "/admin/doctors?filter=attention", tint: "amber",
      valueOf: (s) => s.totals.profiles_needing_attention },
];

const TINTS = {
    blue: "text-[#3A80F7] bg-[#EEF4FE]",
    green: "text-[#16A34A] bg-[#E7F7EE]",
    amber: "text-[#B45309] bg-[#FEF3C7]",
    red: "text-[#DC2626] bg-[#FEE2E2]",
};

export default function AdminOperations() {
    const [summary, setSummary] = useState(null);
    useEffect(() => {
        api.get("/admin/metrics/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(errMsg(e, "Failed")));
    }, []);

    return (
        <AdminShell>
            <div className="mb-6" data-testid="admin-operations-header">
                <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Operations Center</h1>
                <p className="text-[#64748B] mt-1 text-[14px]">Every workflow bucket, one click away.</p>
            </div>
            {!summary ? (
                <div className="text-[#64748B]">Loading…</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {CARDS.map((c) => {
                        const Icon = c.icon;
                        const val = c.valueOf(summary) || 0;
                        return (
                            <Link key={c.key} to={c.path} data-testid={`ops-card-${c.key}`}>
                                <Card className="border-[#E5E9F2] ddi-shadow-sm hover:border-[#3A80F7] hover:shadow transition-all">
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className={"h-10 w-10 rounded-lg flex items-center justify-center " + (TINTS[c.tint] || TINTS.blue)}>
                                                <Icon size={16} />
                                            </div>
                                            <ArrowRight size={12} className="text-[#94A3B8] mt-1" />
                                        </div>
                                        <div className="text-[28px] font-bold text-[#0F172A] leading-none tracking-tight">{val}</div>
                                        <div className="text-[12px] text-[#64748B] font-medium mt-1">{c.label}</div>
                                    </CardContent>
                                </Card>
                            </Link>
                        );
                    })}
                </div>
            )}
        </AdminShell>
    );
}
